import * as React from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import Linkify from 'react-linkify';

import { ReactComponent as Chevron } from 'src/images/chevron-arrow.svg';

interface IQueryProps {
  answer: string;
  ariaId: string;
  isOpen: boolean;
  question: string;
  toggle: () => void;
}

const QueryContainer = styled.dt`
  font-family: inherit;
  line-height: 22px;
  overflow: hidden;
  transition: all 400ms ease-out;
`;

// $isOpen is a transient prop: https://styled-components.com/docs/api#transient-props
const FAQArrow = styled(Chevron)<{ $isOpen: boolean }>`
  display: inline-block;
  width: 11px;
  margin: 5px 7px 0 0;
  padding: 0 0 0 2px;
  transform-style: preserve-3d;
  transform: rotateZ(${({ $isOpen }) => ($isOpen ? 90 : 0)}deg);
  transition: transform 400ms ease 0s;

  path {
    fill: ${({ theme }) => theme.merchantColor};
  }
`;

const FAQButton = styled.button`
  height: inherit;
  min-height: inherit;
  width: 100%;
  display: flex;
  align-items: flex-start;
  font-family: inherit;
  font-weight: 500;
  font-size: 13px;
  padding: 14px 0;
  @media (min-width: 551px) {
    font-size: 16px;
  }
  color: var(--black);
  line-height: inherit;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const FAQQuestion = styled.div`
  max-width: 90%;
  text-align: left;
`;

const FAQAnswer = styled.dd<{ isOpen: boolean }>`
  height: auto;
  max-height: ${({ isOpen }) => (isOpen ? 1000 : 0)}px;
  font-weight: 400;
  line-height: 1.3em;
  font-size: 13px;
  @media (min-width: 551px) {
    font-size: 14px;
  }

  margin: 0 0 ${({ isOpen }) => (isOpen ? 10 : 0)}px 18px;
  overflow: hidden;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  // Visibility must start as hidden for ADA reasons!
  visibility: hidden;
  // NOTE: transition duration is actually set in the component instead, so that visibility
  // toggling is synced up with the animation
  transition-duration: 0.5s;
  transition-timing-function: ${({ isOpen }) =>
    isOpen ? 'ease-in-out' : 'cubic-bezier(0, 1, 0, 1)'};
  transition-property: ${({ isOpen }) => (isOpen ? '' : 'opacity, ')}max-height;
`;

const Query = (props: IQueryProps) => {
  const { answer, ariaId, isOpen, question, toggle } = props;

  const answerRef = React.useRef<HTMLElement>(null);
  const visibilityTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // For ADA:
  // If content is invisible, it needs to be indicated with display:none or visibility:hidden.
  // Otherwise, screen readers will try to read the hidden text
  const animationTimingMs = 500;
  React.useEffect(() => {
    if (!answerRef.current) {
      return;
    }

    // Set this in code instead of CSS so that everything is synced
    answerRef.current.style.transitionDuration = `${animationTimingMs}ms`;

    if (isOpen) {
      // When opening, make visible right away...
      answerRef.current.style.visibility = 'visible';
      // ...and give focus to the selected answer...
      answerRef.current.focus();
      // ...and cancel the visibility = 'hidden' timeout
      if (visibilityTimerRef.current) {
        clearTimeout(visibilityTimerRef.current);
      }
    } else {
      // When closing, wait until animation finishes before hiding
      visibilityTimerRef.current = setTimeout(() => {
        if (answerRef.current) {
          answerRef.current.style.visibility = 'hidden';
        }
      }, animationTimingMs);
    }
  }, [isOpen]);

  return (
    <>
      <QueryContainer className={classNames('faq-query', { 'faq-query--open': isOpen })}>
        <FAQButton
          aria-controls={ariaId}
          aria-expanded={isOpen}
          id={`${ariaId}-question`}
          className="faq-query__question button-reset"
          onClick={toggle}
        >
          <FAQArrow
            $isOpen={isOpen}
            className="faq-query__question__arrow"
            data-test="faq-arrow-button"
          />
          <FAQQuestion data-test="question" className="faq-query__question__text">
            {question}
          </FAQQuestion>
        </FAQButton>
      </QueryContainer>

      <FAQAnswer
        isOpen={isOpen}
        data-test="faq-answer"
        className={classNames('faq-query-answer', { 'faq-query-answer--open': isOpen })}
        id={ariaId}
        role="region"
        aria-labelledby={`${ariaId}-question`}
        ref={answerRef}
      >
        <Linkify>{answer}</Linkify>
      </FAQAnswer>
    </>
  );
};

export default Query;
